import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "sds_digital_profile";

// Keys for storing values in the digital profile
export const PROFILE_KEYS = {
  DEBT_AMOUNT: "debtAmount",
  ZIP_CODE: "zipCode",
  EMAIL: "email",
  MASKED_EMAIL: "maskedEmail",
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  PHONE: "phone",
};

const DigitalProfileContext = createContext(null);

export const DigitalProfileProvider = ({ children }) => {
  const [profile, setProfileState] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === "object") {
            setProfileState(parsed);
          }
        }
      } catch (e) {
        console.warn("Failed to read digital profile from localStorage:", e);
      }
      setIsLoaded(true);
    }
  }, []);

  // Save entire profile to localStorage
  const saveToStorage = useCallback((newProfile) => {
    if (typeof window !== "undefined") {
      try {
        if (Object.keys(newProfile).length > 0) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newProfile));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.warn("Failed to save digital profile to localStorage:", e);
      }
    }
  }, []);

  // Get a value from the profile
  const getValue = useCallback((key) => {
    return profile[key] ?? null;
  }, [profile]);

  // Set a value in the profile
  const setValue = useCallback((key, value) => {
    setProfileState((prev) => {
      const newProfile = { ...prev };

      if (value === null || value === undefined || value === "") {
        delete newProfile[key];
      } else {
        newProfile[key] = value;
      }

      saveToStorage(newProfile);
      return newProfile;
    });
  }, [saveToStorage]);

  // Set multiple values at once
  const setValues = useCallback((values) => {
    setProfileState((prev) => {
      const newProfile = { ...prev };

      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          delete newProfile[key];
        } else {
          newProfile[key] = value;
        }
      });

      saveToStorage(newProfile);
      return newProfile;
    });
  }, [saveToStorage]);

  // Clear a specific key from the profile
  const clearValue = useCallback((key) => {
    setProfileState((prev) => {
      const newProfile = { ...prev };
      delete newProfile[key];
      saveToStorage(newProfile);
      return newProfile;
    });
  }, [saveToStorage]);

  // Clear the entire profile
  const clearProfile = useCallback(() => {
    setProfileState({});
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.warn("Failed to clear digital profile from localStorage:", e);
      }
    }
  }, []);

  return (
    <DigitalProfileContext.Provider
      value={{
        profile,
        getValue,
        setValue,
        setValues,
        clearValue,
        clearProfile,
        isLoaded,
        KEYS: PROFILE_KEYS,
      }}
    >
      {children}
    </DigitalProfileContext.Provider>
  );
};

export const useDigitalProfile = () => {
  const context = useContext(DigitalProfileContext);
  if (!context) {
    // Return a fallback for components not wrapped in provider
    return {
      profile: {},
      getValue: () => null,
      setValue: () => {},
      setValues: () => {},
      clearValue: () => {},
      clearProfile: () => {},
      isLoaded: true,
      KEYS: PROFILE_KEYS,
    };
  }
  return context;
};

export default DigitalProfileContext;
